<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { transformYearlyFortuneText } from '@/composables/splitSentence';
import YinYangChart from './YinYangChart.vue';

const props = defineProps({
  userName: { type: String, default: '' },
  isMobile: { type: Boolean, default: false },
  data: { type: Object, default: () => ({}) },
});

const 전체요약 = computed(() => props.data?.전체요약 || {});
const 음양조후 = computed(() => 전체요약.value?.음양조후 || {});

// 비율 문자열 파싱 ("+53.4" → 양/뜨거운 우세, "-53.4" → 음/차가운 우세)
const parseRatio = (rawValue) => {
  const str = String(rawValue || '');
  const value = parseFloat(str.replace('+', '').replace('-', '')) || 0;
  const isNegative = str.startsWith('-');
  return { value, isNegative };
};

// 음양 원국 차트 (series: [음의 기운, 양의 기운])
// +: 양 우세 → 양=value, -: 음 우세 → 음=value
const 음양원국 = computed(() => 음양조후.value?.원국 || {});
const 음양원국Series = computed(() => {
  const { value, isNegative } = parseRatio(음양원국.value?.음양확률);
  return isNegative ? [value, 100 - value] : [100 - value, value];
});
const 음양원국Label = computed(() => 음양원국.value?.음양상태 || '');
const 음양원국Percent = computed(() => {
  const { value } = parseRatio(음양원국.value?.음양확률);
  return value ? `${value.toFixed(1)}%` : '';
});

// 음양 행운 차트
const 음양행운 = computed(() => 음양조후.value?.행운 || {});
const 음양행운Series = computed(() => {
  const { value, isNegative } = parseRatio(음양행운.value?.음양확률);
  return isNegative ? [value, 100 - value] : [100 - value, value];
});
const 음양행운Label = computed(() => 음양행운.value?.음양상태 || '');
const 음양행운Percent = computed(() => {
  const { value } = parseRatio(음양행운.value?.음양확률);
  return value ? `${value.toFixed(1)}%` : '';
});

// 조후 원국 차트 (series: [뜨거운 기운, 차가운 기운])
// +: 뜨거운 우세 → 뜨거운=value, -: 차가운 우세 → 차가운=value
const 조후원국Series = computed(() => {
  const { value, isNegative } = parseRatio(음양원국.value?.조후확률);
  return isNegative ? [100 - value, value] : [value, 100 - value];
});
const 조후원국Label = computed(() => 음양원국.value?.조후상태 || '');
const 조후원국Percent = computed(() => {
  const { value } = parseRatio(음양원국.value?.조후확률);
  return value ? `${value.toFixed(1)}%` : '';
});

// 조후 행운 차트
const 조후행운Series = computed(() => {
  const { value, isNegative } = parseRatio(음양행운.value?.조후확률);
  return isNegative ? [100 - value, value] : [value, 100 - value];
});
const 조후행운Label = computed(() => 음양행운.value?.조후상태 || '');
const 조후행운Percent = computed(() => {
  const { value } = parseRatio(음양행운.value?.조후확률);
  return value ? `${value.toFixed(1)}%` : '';
});

const 설명 = computed(() => 음양조후.value?.설명 || '');
const 음양변화 = computed(() => 음양조후.value?.음양변화 || '');
const 조후변화 = computed(() => 음양조후.value?.조후변화 || '');
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detailed-analysis-box yearly-fortune-wrapper" :class="{ mobile: isMobile }">
          <!-- 제목 -->
          <div class="yin-yang__title">
            <i class="yin-yang__icon"></i>
            <span>음양 변화</span>
          </div>

          <!-- 차트 영역 -->
          <div class="yin-yang__chart-wrapper">
            <div class="yin-yang__chart-group">
              <div class="yin-yang__tab">타고난 음양</div>
              <YinYangChart
                :series="음양원국Series"
                :centerLabel="음양원국Label"
                :percent="음양원국Percent"
                :isMobile="isMobile"
              />
            </div>
            <div class="yin-yang__chart-group">
              <div class="yin-yang__tab">2026년의 음양</div>
              <YinYangChart
                :series="음양행운Series"
                :centerLabel="음양행운Label"
                :percent="음양행운Percent"
                :isMobile="isMobile"
              />
            </div>
          </div>

          <!-- 설명 -->
          <div class="yin-yang__desc">
            <span class="yin-yang__desc-row">
              <span class="point">음양균형(陰陽均衡)</span>은&nbsp;
              <span>내향적 음의 기운과 외향적 양의 기운이&nbsp;</span>
              <span>조화롭게 어우러진 상태를 나타내며,&nbsp;</span>
            </span>
            <span class="yin-yang__desc-row">
              <span>에너지의 균형이 잘 잡혀&nbsp;</span><span>상황에 맞는 유연한 대응이&nbsp;</span>
              <span>가능한 이상적인 상태입니다.</span>
            </span>
          </div>

          <!-- 음양변화 박스 -->
          <div class="yin-yang__change-box">
            <div class="yin-yang__change-tab">음양변화</div>
            <div class="yin-yang__change-content">
              <p v-html="transformYearlyFortuneText(음양변화?.요약)"></p>
              <p v-if="음양변화?.특징" v-html="transformYearlyFortuneText(음양변화?.특징)"></p>
              <p
                v-if="음양변화?.사회특성"
                v-html="transformYearlyFortuneText(음양변화?.사회특성)"
              ></p>
              <div v-if="음양변화?.긍정키워드 || 음양변화?.주의키워드" class="keyword-box">
                <div v-if="음양변화?.긍정키워드" class="keyword-row">
                  <span class="keyword-label">긍정</span>
                  <div class="keyword-list">
                    <span
                      v-for="(kw, i) in 음양변화.긍정키워드.split(', ')"
                      :key="i"
                      class="keyword-item positive"
                      >{{ kw }}</span
                    >
                  </div>
                </div>
              </div>
              <div v-if="음양변화?.긍정키워드 || 음양변화?.주의키워드" class="keyword-box">
                <div v-if="음양변화?.주의키워드" class="keyword-row">
                  <span class="keyword-label">주의</span>
                  <div class="keyword-list">
                    <span
                      v-for="(kw, i) in 음양변화.주의키워드.split(', ')"
                      :key="i"
                      class="keyword-item caution"
                      >{{ kw }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="johu-wrapper" :class="{ mobile: isMobile }">
          <!-- 제목 -->
          <div class="johu__title">
            <span>🌤</span>
            <span>조후 변화</span>
          </div>

          <!-- 차트 영역 -->
          <div class="johu__chart-wrapper">
            <div class="johu__chart-group">
              <div class="johu__tab">타고난 조후</div>
              <YinYangChart
                :series="조후원국Series"
                :centerLabel="조후원국Label"
                :percent="조후원국Percent"
                :isMobile="isMobile"
                chartType="johu"
              />
            </div>
            <div class="johu__chart-group">
              <div class="johu__tab">2026년의 조후</div>
              <YinYangChart
                :series="조후행운Series"
                :centerLabel="조후행운Label"
                :percent="조후행운Percent"
                :isMobile="isMobile"
                chartType="johu"
              />
            </div>
          </div>

          <!-- 설명 -->
          <div class="johu__desc">
            <span class="yin-yang__desc-row">
              <span class="point">조후균형(調候均衡)</span>은&nbsp;
              <span class="yin-yang__desc-row">
                <span>에너지의 계절성에서 따뜻함과 차가움,&nbsp;</span>
                <span>건조함과 습함이 적절한 균형을 이루는 상태로,&nbsp;</span>
              </span>
            </span>
            <span class="yin-yang__desc-row">
              계절적 조화가 완성된 이상적인 에너지 상태입니다.
            </span>
          </div>

          <!-- 조후변화 박스 -->
          <div class="johu__change-box">
            <div class="johu__change-tab">조후변화</div>
            <div class="johu__change-content">
              <p v-html="transformYearlyFortuneText(조후변화?.요약)"></p>
              <p v-if="조후변화?.특징" v-html="transformYearlyFortuneText(조후변화?.특징)"></p>
              <p
                v-if="조후변화?.사회특성"
                v-html="transformYearlyFortuneText(조후변화?.사회특성)"
              ></p>
              <div v-if="조후변화?.긍정키워드 || 조후변화?.주의키워드" class="keyword-box">
                <div v-if="조후변화?.긍정키워드" class="keyword-row">
                  <span class="keyword-label">긍정</span>
                  <div class="keyword-list">
                    <span
                      v-for="(kw, i) in 조후변화.긍정키워드.split(', ')"
                      :key="i"
                      class="keyword-item positive"
                      >{{ kw }}</span
                    >
                  </div>
                </div>
              </div>
              <div v-if="조후변화?.긍정키워드 || 조후변화?.주의키워드" class="keyword-box">
                <div v-if="조후변화?.주의키워드" class="keyword-row">
                  <span class="keyword-label">주의</span>
                  <div class="keyword-list">
                    <span
                      v-for="(kw, i) in 조후변화.주의키워드.split(', ')"
                      :key="i"
                      class="keyword-item caution"
                      >{{ kw }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
.yearly-fortune-wrapper {
  padding-bottom: 130px;
}
.yearly-fortune-wrapper.mobile {
  padding: 24px 16px;
  padding-bottom: 24px;
}

.yin-yang {
  &__title {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 5px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    margin-bottom: 24px;
  }

  &__icon {
    display: inline-block;
    width: 22px;
    height: 22px;
    background: url('@manseImages/yearly-fortune/icon_yin_yang.svg') center/contain no-repeat;
  }

  &__chart-wrapper {
    width: 100%;
    display: flex;
    gap: 40px;
    margin-bottom: 24px;

    .mobile & {
      gap: 16px;
    }
  }

  &__chart-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  &__tab {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 12px 30px;
    border-radius: 8px;
    background: rgba(190, 51, 49, 0.05);
    color: #be3331;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;

    .mobile & {
      justify-content: center;
    }
  }

  &__desc {
    display: flex;
    flex-wrap: wrap;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin-bottom: 24px;

    .point {
      color: #c23535;
      font-weight: 700;
    }

    &-row {
      display: flex;
      flex-wrap: wrap;
    }
  }

  &__change-box {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__change-tab {
    width: fit-content;
    padding: 4px 15px;
    border-radius: 12px 12px 0 0;
    background: #c23535;
    color: #fff;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
  }

  &__change-content {
    padding: 20px 16px;
    border-radius: 0 12px 12px 12px;
    background: rgba(194, 53, 53, 0.05);
    display: flex;
    flex-direction: column;
    gap: 12px;

    .mobile & {
      padding: 16px 20px;
    }

    p {
      color: #333;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 400;
      line-height: 24px;
      margin: 0;
    }
  }
}

.keyword-box {
  padding: 16px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.keyword-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.keyword-label {
  flex-shrink: 0;
  padding: 3px 6px;
  border-radius: 4px;
  color: #333;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 700;

  // &.positive {
  //   background: rgba(87, 144, 252, 0.1);
  //   color: #5790fc;
  // }

  // &.caution {
  //   background: rgba(243, 174, 17, 0.1);
  //   color: #f3ae11;
  // }
}

.keyword-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
}

.keyword-item {
  padding: 3px 6px;
  border-radius: 4px;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 700;

  &.positive {
    background: rgba(87, 144, 252, 0.1);
    color: #5790fc;
  }

  &.caution {
    background: rgba(243, 174, 17, 0.1);
    color: #f3ae11;
  }
}

// 조후 영역 스타일
.johu-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  padding-bottom: 130px;
  gap: 24px;

  &.mobile {
    padding: 24px 16px;
    padding-bottom: 24px;
  }
}

.johu {
  &__title {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__chart-wrapper {
    width: 100%;
    display: flex;
    gap: 40px;

    .mobile & {
      gap: 16px;
    }
  }

  &__chart-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  &__tab {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 12px 30px;
    border-radius: 8px;
    background: rgba(190, 51, 49, 0.05);
    color: #be3331;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;

    .mobile & {
      justify-content: center;
    }
  }

  &__desc {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;

    .point {
      color: #c23535;
      font-weight: 700;
    }
  }

  &__change-box {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__change-tab {
    width: fit-content;
    padding: 4px 15px;
    border-radius: 12px 12px 0 0;
    background: #c23535;
    color: #fff;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
  }

  &__change-content {
    padding: 20px 16px;
    border-radius: 0 12px 12px 12px;
    background: rgba(194, 53, 53, 0.05);
    display: flex;
    flex-direction: column;
    gap: 12px;

    .mobile & {
      padding: 16px 20px;
    }

    p {
      color: #333;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 400;
      line-height: 24px;
      margin: 0;
    }
  }
}
</style>
